import { useState, useEffect } from "react";
import { Input, Button, App } from "antd";
import { UserService } from "@/services";

interface VerificationStepProps {
  registrationId: string;
  onNext: () => void;
  onBack: () => void;
}

export default function VerificationStep({
  registrationId,
  onNext,
  onBack,
}: VerificationStepProps) {
  const { message } = App.useApp();
  const [code, setCode] = useState("");
  const [countdown, setCountdown] = useState(30);
  const [isRetryEnabled, setIsRetryEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setIsRetryEnabled(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleResendCode = async () => {
    if (!isRetryEnabled) return;

    // Note: Backend doesn't support resending with registration_id yet
    // User needs to go back and restart registration
    message.warning("請返回上一步重新發送驗證碼");
    onBack();
  };

  const handleSubmit = async () => {
    if (code.length !== 6) {
      message.error("請輸入6位數驗證碼");
      return;
    }

    if (!registrationId) {
      message.error("註冊資訊遺失，請重新開始");
      onBack();
      return;
    }

    setLoading(true);
    try {
      const result = await UserService.verifyRegistration(registrationId, code);

      if (result.success) {
        message.success(
          result.message || "手機號碼綁定成功，開始借用你的第一個循環杯吧！",
        );
        onNext();
      } else {
        // Show error message from service
        message.error(result.error || "驗證失敗");
      }
    } catch (error: unknown) {
      // Handle unexpected errors (network issues, etc.)
      const errorMessage = error instanceof Error ? error.message : "驗證失敗";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(value);
  };

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        backgroundColor: "white",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "12px", flex: 0 }}>
        <Input
          size="large"
          placeholder="驗證碼"
          value={code}
          onChange={handleCodeChange}
          maxLength={6}
          type="tel"
          style={{
            borderColor: "#d9d9d9",
            borderRadius: "4px",
            width: "100%",
          }}
          styles={{
            input: {
              fontSize: "16px",
            },
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          flexShrink: 0,
          height: "60px",
          width: "100%",
          padding: 0,
          margin: 0,
        }}
      >
        <Button
          size="large"
          onClick={onBack}
          style={{
            flex: 3,
            margin: "6px",
            border: "1px solid #FFCD14",
            borderRadius: "4px",
            color: "#FFCD14",
            backgroundColor: "transparent",
            height: "48px",
          }}
        >
          上一步
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleSubmit}
          disabled={code.length !== 6}
          loading={loading}
          style={{
            flex: 5,
            margin: "6px 6px 6px 0",
            borderRadius: "4px",
            backgroundColor: code.length === 6 ? "#FFCD14" : "#BDC1C9",
            borderColor: code.length === 6 ? "#FFCD14" : "#BDC1C9",
            color: "white",
            height: "48px",
          }}
        >
          確定
        </Button>
      </div>

      <div style={{ padding: "0 12px", paddingBottom: "12px", flex: 0 }}>
        <Button
          size="large"
          onClick={handleResendCode}
          disabled={!isRetryEnabled}
          style={{
            width: "100%",
            height: "48px",
            borderRadius: "4px",
            backgroundColor: isRetryEnabled ? "#FFCD14" : "#BDC1C9",
            borderColor: isRetryEnabled ? "#FFCD14" : "#BDC1C9",
            color: "white",
          }}
        >
          {isRetryEnabled ? "重新發送認證碼" : `重新發送認證碼 (${countdown}s)`}
        </Button>
      </div>
    </div>
  );
}
